import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { getPasswordStrength } from '@/bundles/Auth/helpers';
import { MIN_CHAR_COUNT } from '@/bundles/App/consts';
import { ValidationFunc } from '@/types/common';

import { formatRawNumber } from './filters';

export const urlPattern = /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;

export function validateYouTubeUrl (url: string) {
  return /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S*)?$/.test(url);
}

export function validateVimeoUrl (url: string) {
  return /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:[a-zA-Z0-9_]+\/)?(?:[a-zA-Z0-9_-]+)(?:\S*)?$/.test(url);
}

export const emailPattern = /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/;
export const passwordStrengthValidationRule = (value) => getPasswordStrength(value)?.id === 3 || 'Your password must be at least 12 characters long and include uppercase letters, lowercase letters, numbers, and special symbols.';

// for a field shown with PasswordRequirements, which names what is missing
export const passwordRequirementsRule = (value) => passwordStrengthValidationRule(value) === true || 'Meet every requirement below.';

export const minAgainstMaxValidation = (value, max) => {
  const valueNumber = formatRawNumber(value) || 0;
  const maxNumber = formatRawNumber(max) || 0;

  return !isValuePresent(value) || !isValuePresent(max) || valueNumber <= maxNumber || 'Min Should Be Less Or Equal Max';
};

export const maxAgainstMinValidation = (value, min) => {
  const valueNumber = formatRawNumber(value) || 0;
  const minNumber = formatRawNumber(min) || 0;
  return !isValuePresent(value) || valueNumber >= minNumber || 'Max Should Be Larger Or Equal Min';
};

export const csvFileValidationRule = (file: File) => {
  const fileName = file.name.toLowerCase();
          const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
          const isValidExtension = fileExtension === '.csv';
          const isValidMimeType = file.type === 'text/csv' ||
                                  file.type === 'application/csv' ||
                                  file.type === 'application/vnd.ms-excel' ||
                                  file.type === '';

  if (file.type === '') {
    return isValidExtension || 'Only CSV files are allowed';
  }

  if (!isValidExtension || !isValidMimeType) {
    return 'Only CSV files are allowed';
  }

  return true;
};

export const validationRules = {
    getRequiredRule: () => (value) => !!value || 'Required.',
    // for text the value gets trimmed before it is stored: '   ' is truthy, so getRequiredRule lets it
    // through and the record is saved with an empty name
    getRequiredTextRule: (): ValidationFunc => (value: unknown) => !!String(value ?? '').trim() || 'Required.',
    // Vuetify validates raw input text: numeric strings pass; '' / null / NaN fail, 0 passes
    getRangeRules: (label: string, min: number, max: number): ValidationFunc[] => [
      (value: unknown) => (value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))) || `${label} is required`,
      (value: unknown) => (Number(value) >= min && Number(value) <= max) || `${label} must be between ${min} and ${max}`,
    ],
    getMinRule: (min: number) => (v) => (v && v.length >= min) || `Min ${min} characters`,
    getLinkedIdUrl: () => (v) => {
      if (v) {
        const regex = /^https:\/\/www\.linkedin\.com\/in\/[a-zA-Z0-9-]+\/$/;
        return regex.test(v) || 'Invalid LinkedIn URL';
      }
      return true;
    }
};
